import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import { Tooltip } from '@material-ui/core'
import { getIconUrl, isIconPadded } from '../../../../components/pages/denali/Catalog/shared/catalogIcons'

const useStyles = makeStyles(() => ({
  card: {
    borderRadius: 'var(--border-radius-md)',
    minHeight: '100px',
    width: '130px',
    border: '1px solid var(--dai-color-border)',
    boxShadow: '0px 4px 6px -2px rgba(0,0,0,0.3)',
    backgroundColor: 'var(--dai-color-surface3)',
    backgroundImage: ({ imageUrl }) => imageUrl ? `url(${imageUrl})` : undefined,
    backgroundPosition: ({ imagePadded }) => imagePadded ? 'center 40%' : 'center 30%',
    backgroundSize: ({ imagePadded }) => imagePadded ? '50%' : 'cover',
    backgroundRepeat: 'no-repeat',
    position: 'relative',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0px 6px 10px -2px rgba(0,0,0,0.4)'
    }
  },
  title: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bottom: 0,
    left: 0,
    right: 0,
    height: '32px',
    color: 'var(--color-primary-050)',
    fontSize: '0.7rem',
    fontWeight: 500,
    fontFamily: 'var(--font-sans)',
    padding: '0 6px',
    borderBottomLeftRadius: 'var(--border-radius-md)',
    borderBottomRightRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--color-primary-500)',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  countBadge: {
    position: 'absolute',
    top: '6px',
    right: '6px',
    backgroundColor: 'var(--dai-color-accent)',
    color: 'white',
    fontSize: '0.65rem',
    fontWeight: 600,
    fontFamily: 'var(--font-sans)',
    padding: '2px 6px',
    borderRadius: '10px',
    minWidth: '20px',
    textAlign: 'center'
  }
}))

/**
 * Compact card component for displaying a data source with its catalog icon
 * Used in the AI chat to visualize which data sources are being accessed
 */
function DataSourceCard ({ catalogDatasource, name, type, count }) {
  // Create a synthetic datasource for icon resolution if no catalog match
  const datasourceForIcon = catalogDatasource || {
    type: type === 'view' ? 'table' : 'api',
    icon: type === 'view' ? 'table' : 'api'
  }

  const imageUrl = getIconUrl(datasourceForIcon)
  const imagePadded = isIconPadded(datasourceForIcon)
  const classes = useStyles({ imageUrl, imagePadded })

  // Use catalog display name if available, otherwise format the name
  const displayName = catalogDatasource?.displayName || formatName(name)

  return (
    <Tooltip title={name} placement='top'>
      <div className={classes.card}>
        {count > 1 && (
          <div className={classes.countBadge}>
            ×{count}
          </div>
        )}
        <div className={classes.title}>
          {displayName}
        </div>
      </div>
    </Tooltip>
  )
}

/**
 * Format a raw data source name for display
 * e.g., "fact.client_cost" -> "client_cost", "get_client_details" -> "get_client_details"
 */
function formatName (name) {
  if (!name) return 'Unknown'
  // For schema.code format, take the code part
  const parts = name.split('.')
  return parts.length > 1 ? parts[parts.length - 1] : name
}

DataSourceCard.propTypes = {
  /** The catalog datasource object (if matched), used for display name and icon */
  catalogDatasource: PropTypes.shape({
    displayName: PropTypes.string,
    icon: PropTypes.string,
    type: PropTypes.string
  }),
  /** The raw name from tool call summary (e.g., "fact.client_cost" or "get_client_details") */
  name: PropTypes.string.isRequired,
  /** The data source type: 'view' or 'api' */
  type: PropTypes.oneOf(['view', 'api']).isRequired,
  /** Number of times this data source was accessed */
  count: PropTypes.number
}

DataSourceCard.defaultProps = {
  count: 1
}

export default DataSourceCard
